import { useCallback, useEffect, useState } from 'react';
import { useAPI } from '../../../../clients/quipli-service/quipli-service-client';
import { SelectForm } from '../../../../components/select-form/select-form';
import { Field } from '../../../../components/field/field';
import { DurationUnit } from '../../../../clients/quipli-service/pricing/pricing';
import { Typography } from '../../../../components/typography/typography';
import { Button } from '../../../../components/form/button';
import Icon from '../../../../components/icons/icon';
import { debounce } from 'lodash';

export const MinimumDurationPriceConfigForm = ({ productId }: { productId?: number }) => {
  const { getProductMinimumDurationPrice, upsertProductMinimumDurationPrice, deleteProductMinimumDurationPrice } =
    useAPI();

  const [isLoading, setIsLoading] = useState(false);
  const [durationValue, setDurationValue] = useState<number>();
  const [durationUnit, setDurationUnit] = useState<{ label: string; value: DurationUnit } | null>(null);
  const unitOptions = [
    { label: 'Hour', value: DurationUnit.hour },
    { label: 'Day', value: DurationUnit.day },
    { label: 'Week', value: DurationUnit.week },
    { label: 'Month', value: DurationUnit.month },
    { label: 'Year', value: DurationUnit.year },
  ];
  const hasMinimumDurationPrice = durationValue && durationUnit;

  const handleOnChange = useCallback(
    debounce(async (_unit?: { label: string; value: DurationUnit }, _duration?: number) => {
      if (_unit && _duration && productId) {
        setIsLoading(true);

        const result = await upsertProductMinimumDurationPrice({
          productId,
          durationUnit: _unit.value,
          durationValue: _duration,
        });

        setDurationUnit(unitOptions.find((option) => option.value === result.durationUnit) || null);
        setDurationValue(result.durationValue);

        setIsLoading(false);
      } else {
        setDurationUnit(_unit || null);
        setDurationValue(_duration);
      }
    }, 500),
    [productId],
  );

  const handleOnDelete = async () => {
    if (!productId) return;
    await deleteProductMinimumDurationPrice(productId);
    setDurationUnit(null);
    setDurationValue(undefined);
  };

  const fetchMinimumDurationPrice = async () => {
    if (!productId) return;
    setIsLoading(true);

    const result = await getProductMinimumDurationPrice(productId);

    setDurationUnit(unitOptions.find((option) => option.value === result?.durationUnit) || null);
    setDurationValue(result?.durationValue);
    setIsLoading(false);
  };

  useEffect(() => {
    fetchMinimumDurationPrice();
  }, [productId]);

  return (
    <div className="flex flex-col max-w-sm md:max-w-md">
      <div className="flex items-center gap-2">
        <SelectForm
          label="Unit"
          labelType="text-xs"
          placeholder="Select unit duration"
          type="inline"
          value={durationUnit}
          options={unitOptions}
          onChange={(newUnit) => handleOnChange(newUnit || undefined, durationValue)}
          isLoading={isLoading}
          isDisabled={isLoading}
        />

        <Field
          label="Quantity"
          labelType="text-xs"
          type="number"
          value={durationValue}
          onChange={(newValue: number) => handleOnChange(durationUnit || undefined, newValue)}
          disabled={isLoading}
        />

        {hasMinimumDurationPrice && (
          <Button className="icon-button" onClick={handleOnDelete} disabled={isLoading}>
            <Icon icon="Trash" stroke="var(--error-500)" />
          </Button>
        )}
      </div>

      <Typography type="text-xs" className="!italic">
        If a rental is submitted for less than this duration, the price for the rental will be calculated based on this
        minimum duration.
      </Typography>
    </div>
  );
};
